import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import moment from 'moment';

@Component({
  selector: 'app-facebook-post',
  templateUrl: './facebook-post.component.html',
  styleUrl: './facebook-post.component.css',
  standalone: false
})
export class FacebookPostComponent implements OnChanges {

  @Input() webinar: any;
  @Input() imageFile: any;

  @ViewChild('canvas') canvas!: ElementRef<HTMLCanvasElement>;
  @Output() facebookPostGenerated = new EventEmitter<any>();

  topImageData!: string;
  finalImage!: any;

  ngOnChanges(changes: SimpleChanges): void {

    // ❗ LOGIC UNCHANGED
    if (changes['imageFile'] && this.imageFile) {
      const reader = new FileReader();
      reader.onload = () => {
        this.topImageData = reader.result as string;
        this.generateImage();
      };
      reader.readAsDataURL(this.imageFile.file);
    }

    // ❗ LOGIC UNCHANGED
    if (changes['webinar'] && this.topImageData) {
      this.generateImage();
    }
  }

  async generateImage() {

    // ❗ ALL GUARDS UNCHANGED
    if (!this.webinar || !this.topImageData) return;
    if (!this.canvas) return;
    if (
      !this.webinar.Name ||
      !this.webinar.StartDate ||
      !this.webinar.NumberOfSeat ||
      !this.webinar.Description ||
      !this.webinar.StartHour
    ) return;

    const canvas = this.canvas.nativeElement;
    const ctx = canvas.getContext('2d')!;

    const width = 420;
    const headerHeight = 90;
    const imageHeight = 260;
    const metaHeight = 260;

    canvas.width = width;
    canvas.height = headerHeight + imageHeight + metaHeight;

    // ================= BACKGROUND =================
    ctx.fillStyle = '#0d2a64';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // ================= HEADER =================
    ctx.fillStyle = '#d7ef2b';
    ctx.fillRect(0, 0, width, headerHeight);

    ctx.fillStyle = '#000';
    ctx.font = 'bold 22px Inter, Arial';
    ctx.textAlign = 'center';
    ctx.fillText(
      this.webinar.Name,
      width / 2,
      headerHeight / 2 + 8
    );
    ctx.textAlign = 'left';

    // ================= IMAGE =================
    const img = await this.loadImage(this.topImageData);

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 22;
    ctx.shadowOffsetY = 14;

    this.drawRoundedRect(
      ctx,
      20,
      headerHeight + 16,
      width - 40,
      imageHeight - 32,
      18
    );
    ctx.clip();
    ctx.drawImage(
      img,
      20,
      headerHeight + 16,
      width - 40,
      imageHeight - 32
    );
    ctx.restore();

    // ================= FIXED NEW RIBBON =================
    this.drawRibbon(ctx);

    // ================= DETAILS CARD =================
    const cardY = headerHeight + imageHeight + 12;

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 14;
    ctx.fillStyle = '#ffffff';

    this.drawRoundedRect(
      ctx,
      16,
      cardY,
      width - 32,
      metaHeight - 24,
      20
    );
    ctx.fill();
    ctx.restore();

    let y = cardY + 32;

    ctx.font = 'bold 14px Inter, Arial';
    ctx.fillStyle = '#555';
    ctx.fillText(
      `📅 ${moment(this.webinar.StartDate).format('DD MMM YYYY')} ${this.webinar.StartHour}`,
      32,
      y
    );

    y += 20;
    ctx.font = 'bold 14px Inter, Arial';
    ctx.fillStyle = '#555';
    ctx.fillText(
      `🎟 Seats: ${this.webinar.NumberOfSeat}    💲 ₹${this.webinar.Price}`,
      32,
      y
    );

    y += 18;
    ctx.strokeStyle = '#eee';
    ctx.beginPath();
    ctx.moveTo(32, y);
    ctx.lineTo(width - 32, y);
    ctx.stroke();

    y += 22;
    ctx.font = 'bold 16px Inter, Arial';
    ctx.fillStyle = '#222';
    ctx.fillText('Webinar Description', 32, y);

    y += 18;
    ctx.font = '13px Inter, Arial';
    ctx.fillStyle = '#444';

    this.drawWrappedText(
      ctx,
      this.webinar.Description,
      32,
      y,
      width - 64,
      18
    );

    // ================= BORDER =================
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1.2;
    this.drawRoundedBorder(ctx, width, canvas.height, 18, 1.2);

    // ================= EXPORT =================
    this.finalImage = canvas.toDataURL('image/png');

    canvas.toBlob((blob) => {
      if (!blob) return;

      const file = new File(
        [blob],
        'facebook-post.png',
        { type: 'image/png' }
      );

      this.facebookPostGenerated.emit(file);

    }, 'image/png', 1.0);
  }

  // ================= RIBBON (FIXED) =================
  private drawRibbon(ctx: CanvasRenderingContext2D) {

    ctx.save();

    // Move origin to top-left corner
    ctx.translate(0, 0);

    // Rotate ribbon
    ctx.rotate(-Math.PI / 4);

    const gradient = ctx.createLinearGradient(0, 0, 180, 0);
    gradient.addColorStop(0, '#ff3d00');
    gradient.addColorStop(1, '#ff9100');

    ctx.fillStyle = gradient;
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 10;

    // Ribbon body
    ctx.fillRect(-60, 20, 250, 34);

    // Text
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px Inter, Arial';
    ctx.textAlign = 'center';
    ctx.fillText('NEW                         ', 50, 43);

    ctx.restore();
  }

  // ================= HELPERS (UNCHANGED) =================

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  private drawWrappedText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number
  ) {
    const words = text.split(' ');
    let line = '';

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxWidth && i > 0) {
        ctx.fillText(line, x, y);
        line = words[i] + ' ';
        y += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, y);
  }

  private drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  private drawRoundedBorder(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    radius: number,
    lineWidth: number
  ) {
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(width - radius, 0);
    ctx.quadraticCurveTo(width, 0, width, radius);
    ctx.lineTo(width, height - radius);
    ctx.quadraticCurveTo(width, height, width - radius, height);
    ctx.lineTo(radius, height);
    ctx.quadraticCurveTo(0, height, 0, height - radius);
    ctx.lineTo(0, radius);
    ctx.quadraticCurveTo(0, 0, radius, 0);
    ctx.closePath();
    ctx.stroke();
  }
}
